// WebRTCManager.ts
interface SignalMessage {
  type: "offer" | "answer" | "candidate" | "register" | "end-call";
  source?: string;
  target?: string;
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
}

interface WebRTCConfig {
  coturnSecret: string;
  turnServerIP: string;
  wsServer: string;
}

interface WebRTCCallbacks {
  onWsReady?: (ready: boolean) => void;
  onCameraStarted?: (started: boolean) => void;
  onIncomingCall?: (incoming: boolean) => void;
  onCallActive?: (active: boolean) => void;
  onScreenSharing?: (sharing: boolean) => void;
  onConnectionState?: (state: string) => void;
  onIceConnectionState?: (state: string) => void;
  onLocalStream?: (stream: MediaStream | null) => void;
  onRemoteStream?: (stream: MediaStream | null) => void;
}

// TURN credential generator
async function generateTurnCredentials(
  secret: string,
  user: string = "testuser",
  ttlSeconds: number = 3600
) {
  const expiry = Math.floor(Date.now() / 1000) + ttlSeconds;
  const username = `${expiry}:${user}`;

  const enc = new TextEncoder();
  const keyData = enc.encode(secret);

  const key = await window.crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"]
  );

  const signature = await window.crypto.subtle.sign(
    "HMAC",
    key,
    enc.encode(username)
  );
  const hashArray = Array.from(new Uint8Array(signature));
  const credential = btoa(
    hashArray.map((b) => String.fromCharCode(b)).join("")
  );

  return { username, credential, ttl: ttlSeconds };
}

export class WebRTCManager {
  private config: WebRTCConfig;
  private callbacks: WebRTCCallbacks;

  private pc: RTCPeerConnection | null = null;
  private ws: WebSocket | null = null;
  private localStream: MediaStream | null = null;

  private sourceId: string;
  private targetId: string = "";
  private autoSourceId: string;

  private turnCreds = { username: "", credential: "" };
  private iceQueue: RTCIceCandidateInit[] = [];
  private remoteDescriptionSet = false;
  private pendingOffer: RTCSessionDescriptionInit | null = null;

  private cameraStarted = false;
  private isScreenSharing = false;
  private callActive = false;

  remoteVideoElement: HTMLVideoElement | null = null;


  constructor(config: WebRTCConfig, callbacks: WebRTCCallbacks = {}) {
    this.config = config;
    this.callbacks = callbacks;
    this.autoSourceId = "client-" + Math.floor(Math.random() * 99999);
    this.sourceId = this.autoSourceId;

    this.initializeTurnCredentials();
  }

  private async initializeTurnCredentials() {
    try {
      const creds = await generateTurnCredentials(this.config.coturnSecret);
      this.turnCreds = creds;
      console.log("✅ TURN credentials generated:", creds.username);
    } catch (err) {
      console.error("❌ Failed to generate TURN credentials:", err);
    }
  }

  private getRtcConfig(): RTCConfiguration {
    return {
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        ...(this.turnCreds.username
          ? [
            {
              urls: [
                `turn:${this.config.turnServerIP}:3478?transport=udp`,
                `turn:${this.config.turnServerIP}:3478?transport=tcp`,
              ],
              username: this.turnCreds.username,
              credential: this.turnCreds.credential,
            },
          ]
          : []),
      ],
      iceTransportPolicy: "all" as RTCIceTransportPolicy,
    };
  }

  private sendSignal(msg: any) {
    const finalSource = this.sourceId.trim() || this.autoSourceId;
    const finalTarget = this.targetId.trim();

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("⚠️ WS not ready");
      return;
    }

    const payload = {
      ...msg,
      source: finalSource,
      target: finalTarget,
    };

    console.log("📤 Sending:", payload.type, "to", finalTarget);
    this.ws.send(JSON.stringify(payload));
  }

  public async initWebSocket() {
    return new Promise<void>((resolve, reject) => {
      this.ws = new WebSocket(this.config.wsServer);

      this.ws.onopen = () => {
        this.callbacks.onWsReady?.(true);
        const finalSource = this.sourceId.trim() || this.autoSourceId;

        this.ws!.send(
          JSON.stringify({
            type: "register",
            clientId: finalSource,
          })
        );

        console.log("✅ Registered as:", finalSource);
        resolve();
      };

      this.ws.onclose = () => {
        console.log("🔴 WebSocket closed");
        this.callbacks.onWsReady?.(false);
      };

      this.ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
        reject(error);
      };

      this.ws.onmessage = async (event) => {
        const data: SignalMessage = JSON.parse(event.data);
        const finalSource = this.sourceId.trim() || this.autoSourceId;

        if (data.target && data.target !== finalSource) return;

        console.log("📥 Received:", data.type, "from", data.source);

        if (!this.pc) {
          console.warn("⚠️ PeerConnection not initialized");
          return;
        }

        try {
          if (data.type === "offer" && data.offer) {
            this.pendingOffer = data.offer;
            this.callbacks.onIncomingCall?.(true);
          }

          if (data.type === "answer" && data.answer) {
            console.log("📥 Setting remote answer");
            await this.pc.setRemoteDescription(
              new RTCSessionDescription(data.answer)
            );
            this.remoteDescriptionSet = true;
            this.callActive = true;
            this.callbacks.onCallActive?.(true);

            // Process queued ICE candidates
            console.log(
              `🧊 Processing ${this.iceQueue.length} queued ICE candidates`
            );
            while (this.iceQueue.length > 0) {
              const c = this.iceQueue.shift()!;
              try {
                await this.pc.addIceCandidate(new RTCIceCandidate(c));
              } catch (err) {
                console.error("❌ Error adding queued ICE candidate:", err);
              }
            }
          }

          if (data.type === "candidate" && data.candidate) {
            if (!this.remoteDescriptionSet) {
              console.log("🧊 Queueing ICE candidate (no remote desc yet)");
              this.iceQueue.push(data.candidate);
            } else {
              console.log("🧊 Adding ICE candidate immediately");
              try {
                await this.pc.addIceCandidate(
                  new RTCIceCandidate(data.candidate)
                );
              } catch (err) {
                console.error("❌ Error adding ICE candidate:", err);
              }
            }
          }

          if (data.type === "end-call") {
            console.log("📥 Remote ended the call");
            this.stopCall();
            return;
          }
        } catch (err) {
          console.error("❌ Error handling message:", err);
        }
      };
    });
  }

  private initPeerConnection() {
    if (this.pc) {
      console.log("♻️ PeerConnection already exists");
      return;
    }

    console.log("🔄 Creating new PeerConnection");
    this.pc = new RTCPeerConnection(this.getRtcConfig());

    // Connection state monitoring
    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState || "";
      console.log("🔗 Connection state:", state);
      this.callbacks.onConnectionState?.(state);
    };

    this.pc.oniceconnectionstatechange = () => {
      const state = this.pc?.iceConnectionState || "";
      console.log("🧊 ICE connection state:", state);
      this.callbacks.onIceConnectionState?.(state);
    };

    this.pc.onicegatheringstatechange = () => {
      console.log("🧊 ICE gathering state:", this.pc?.iceGatheringState);
    };

    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log("🧊 Local ICE candidate:", event.candidate.candidate);
        this.sendSignal({
          type: "candidate",
          candidate: event.candidate.toJSON(),
        });
      } else {
        console.log("✅ ICE gathering complete");
      }
    };

    this.pc.ontrack = (event) => {
      console.log("📺 Received remote track:", event.track.kind, event.track.id);

      // Create a new MediaStream with the received track
      const stream = new MediaStream([event.track]);
      this.callbacks.onRemoteStream?.(stream);
    };
  }

  public async startCamera(withVideo: boolean = true) {
    try {
      this.initPeerConnection();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: withVideo ? { width: 1280, height: 720 } : false,
        audio: true,
      });

      this.localStream = stream;
      this.callbacks.onLocalStream?.(stream);

      // Add tracks to peer connection
      stream.getTracks().forEach((track) => {
        console.log("➕ Adding local track:", track.kind);
        this.pc!.addTrack(track, stream);
      });

      this.cameraStarted = true;
      this.isScreenSharing = false;
      this.callbacks.onCameraStarted?.(true);
      this.callbacks.onScreenSharing?.(false);
      console.log("✅ Camera started");
    } catch (err) {
      console.error("❌ Camera error:", err);
      throw err;
    }
  }

  public async startScreenShare() {
  try {
    this.initPeerConnection();

    // 1. Get screen share WITH system audio
    const screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,  // ← Enable system/tab audio
    });

    // 2. Also get microphone audio so the user can talk
    let micStream: MediaStream | null = null;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });
    } catch (micErr) {
      console.warn("⚠️ Could not get microphone:", micErr);
      // Continue without mic - screen audio will still work
    }

    // 3. Combine all tracks into one stream for local preview
    const combinedStream = new MediaStream();
    
    // Add screen video track
    screenStream.getVideoTracks().forEach((track) => {
      combinedStream.addTrack(track);
      this.pc!.addTrack(track, combinedStream);
      console.log("➕ Adding screen video track:", track.kind);
    });

    // Add screen audio track (system/tab audio) if available
    screenStream.getAudioTracks().forEach((track) => {
      combinedStream.addTrack(track);
      this.pc!.addTrack(track, combinedStream);
      console.log("➕ Adding screen audio track:", track.kind);
    });

    // Add microphone audio track if available
    if (micStream) {
      micStream.getAudioTracks().forEach((track) => {
        combinedStream.addTrack(track);
        this.pc!.addTrack(track, combinedStream);
        console.log("➕ Adding microphone track:", track.kind);
      });
    }

    this.localStream = combinedStream;
    this.callbacks.onLocalStream?.(combinedStream);

    // Stop when user clicks "Stop Sharing"
    screenStream.getVideoTracks()[0].onended = () => {
      console.log("🛑 Screen sharing ended by user");
      // Also stop mic if it was started
      if (micStream) {
        micStream.getTracks().forEach(t => t.stop());
      }
      this.stopCall();
    };

    this.cameraStarted = true;
    this.isScreenSharing = true;
    this.callbacks.onCameraStarted?.(true);
    this.callbacks.onScreenSharing?.(true);
    console.log("✅ Screen share started with audio");
  } catch (err) {
    console.error("❌ Screen share error:", err);
    throw err;
  }
}

  public async call() {
    if (!this.targetId.trim()) {
      throw new Error("Target ID is required");
    }

    if (!this.cameraStarted) {
      throw new Error("Start camera/screen first");
    }

    try {
      this.initPeerConnection();

      console.log("📞 Creating offer...");
      const offer = await this.pc!.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });

      await this.pc!.setLocalDescription(offer);
      console.log("✅ Local description set");

      this.sendSignal({ type: "offer", offer });
      this.callActive = true;
      this.callbacks.onCallActive?.(true);
    } catch (err) {
      console.error("❌ Call error:", err);
      throw err;
    }
  }

  public async attendCall() {
    if (!this.pendingOffer) {
      throw new Error("No pending offer");
    }

    if (!this.cameraStarted) {
      throw new Error("Start camera/screen first");
    }

    try {
      console.log("📞 Attending call...");
      await this.pc!.setRemoteDescription(
        new RTCSessionDescription(this.pendingOffer)
      );
      console.log("✅ Remote description set");

      const answer = await this.pc!.createAnswer();
      await this.pc!.setLocalDescription(answer);
      console.log("✅ Answer created");

      this.sendSignal({ type: "answer", answer });

      this.remoteDescriptionSet = true;
      this.callbacks.onIncomingCall?.(false);
      this.callActive = true;
      this.callbacks.onCallActive?.(true);

      // Process any queued ICE candidates
      console.log(`🧊 Processing ${this.iceQueue.length} queued ICE candidates`);
      while (this.iceQueue.length > 0) {
        const c = this.iceQueue.shift()!;
        try {
          await this.pc!.addIceCandidate(new RTCIceCandidate(c));
        } catch (err) {
          console.error("❌ Error adding queued ICE candidate:", err);
        }
      }
    } catch (err) {
      console.error("❌ Attend call error:", err);
      throw err;
    }
  }

  public stopCall() {
    console.log("🛑 Stopping call...");

    // Notify remote
    if (this.ws && this.targetId.trim()) {
      this.ws.send(
        JSON.stringify({
          type: "end-call",
          source: this.sourceId,
          target: this.targetId,
        })
      );
    }

    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.stop();
        console.log("🛑 Stopped local track:", track.kind);
      });
      this.localStream = null;
    }

    this.callbacks.onLocalStream?.(null);
    this.callbacks.onRemoteStream?.(null);

    this.cameraStarted = false;
    this.callActive = false;
    this.isScreenSharing = false;

    this.callbacks.onCameraStarted?.(false);
    this.callbacks.onIncomingCall?.(false);
    this.callbacks.onCallActive?.(false);
    this.callbacks.onScreenSharing?.(false);
    this.callbacks.onConnectionState?.("");
    this.callbacks.onIceConnectionState?.("");

    this.pendingOffer = null;
    this.remoteDescriptionSet = false;
    this.iceQueue = [];

    console.log("✅ Call stopped");
  }

  setRemoteVideoElement(videoEl: HTMLVideoElement) {
    this.remoteVideoElement = videoEl;
  }


  muteMic(isMuted: boolean) {
    if (!this.localStream) return;

    this.localStream.getAudioTracks().forEach(track => {
      track.enabled = !isMuted;
    });

    console.log(isMuted ? "🎤 Mic Muted" : "🎤 Mic Unmuted");
  }

  muteSpeaker(isMuted: boolean) {
    if (this.remoteVideoElement) {
      this.remoteVideoElement.muted = isMuted;
    }
  }



  public cleanup() {
    console.log("🧹 Cleaning up WebRTC Manager...");

    // Notify remote about ending the call
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.targetId.trim()) {
      try {
        this.ws.send(
          JSON.stringify({
            type: "end-call",
            source: this.sourceId,
            target: this.targetId,
          })
        );
        console.log("📤 Sent end-call signal to:", this.targetId);
      } catch (err) {
        console.error("❌ Error sending end-call:", err);
      }
    }

    // Stop all media tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.stop();
        console.log("🛑 Stopped track on cleanup:", track.kind);
      });
      this.localStream = null;
    }

    // Close peer connection
    if (this.pc) {
      this.pc.close();
      this.pc = null;
      console.log("🛑 Closed peer connection on cleanup");
    }

    // Close WebSocket
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.close();
          console.log("🛑 Closed WebSocket on cleanup");
        }
      }, 100);
    }

    console.log("✅ Cleanup complete");
  }

  // Getters and Setters
  public setSourceId(id: string) {
    this.sourceId = id;
  }

  public setTargetId(id: string) {
    this.targetId = id;
  }

  public getSourceId(): string {
    return this.sourceId;
  }

  public getTargetId(): string {
    return this.targetId;
  }

  public getAutoSourceId(): string {
    return this.autoSourceId;
  }

  public isCameraStarted(): boolean {
    return this.cameraStarted;
  }

  public isCallActive(): boolean {
    return this.callActive;
  }

  public isScreenSharingActive(): boolean {
    return this.isScreenSharing;
  }
}

export default WebRTCManager;